/**
 * main.tsx — entry MÍNIMO a propósito (ROADMAP §"Performance · prerender de
 * la landing"). El grafo real de la app vive en `bootstrap.tsx` y se carga
 * con import() dinámico: si se importara estático, la evaluación de todo el
 * JS inicial bloquea el primer paint y el HTML prerendereado de `/` no
 * aporta nada al LCP (medido: render delay ~3.8s con red instantánea).
 *
 * Acá solo va: el CSS que la landing prerendereada necesita para pintar,
 * el listener de stale-bundle y el gate de hidratación.
 */
import './styles/globals.css'
// El CSS de la landing DEBE ser import estático del entry: si solo lo
// importara LandingPage (dentro del grafo dinámico), Vite lo movería al CSS
// del chunk bootstrap y el prerender pintaría sin estilos hasta que cargue.
// Vite dedupea: no se duplica cuando bootstrap también lo importa.
import './pages/landing/LandingPage.css'

// Stale-bundle recovery for code-split SPAs.
// Cada push deploya nuevos chunks con hashes distintos; un usuario con la app
// abierta de antes del deploy todavía tiene el index.html viejo, así que sus
// imports dinámicos (rutas con React.lazy) apuntan a archivos que ya no existen
// → "Failed to fetch dynamically imported module". Vite dispara este evento
// específico justo cuando eso pasa; un reload trae el index.html nuevo y
// resuelve. Sólo recargamos una vez por sesión para no entrar en loop si el
// problema fuera de red, no de stale.
window.addEventListener('vite:preloadError', () => {
  if (sessionStorage.getItem('reloaded-for-stale-bundle')) return
  sessionStorage.setItem('reloaded-for-stale-bundle', '1')
  window.location.reload()
})

/* Prerender de la landing: dist/index.html trae `/` ya pintada dentro de
 * #root (ES · visitante anónimo). `createRoot().render()` REEMPLAZA ese DOM
 * en el mount y Chrome re-emite el LCP en el repaint — hidratar con
 * `hydrateRoot` adopta el DOM sin repintar y el LCP queda en el primer paint
 * del HTML. Se hidrata SOLO el caso cuyo markup coincide con lo prerendereado;
 * cualquier otro (EN, sesión activa que redirige a dashboard, shell app.html
 * vacío) monta con createRoot como siempre. Un mismatch imprevisto no rompe:
 * React 18 recupera re-renderizando client-side (= replacement). */
function canHydratePrerender(container: HTMLElement): boolean {
  if (!container.hasChildNodes()) return false
  try {
    const lang = localStorage.getItem('i18nextLng') ?? navigator.language ?? 'es'
    if (!lang.toLowerCase().startsWith('es')) return false
    const persisted = localStorage.getItem('brackify-auth')
    if (persisted && JSON.parse(persisted)?.state?.accessToken) return false
    return true
  } catch {
    return false
  }
}

const container = document.getElementById('root')!

if (canHydratePrerender(container)) {
  // Doble rAF: garantiza al menos un frame pintado con el HTML prerendereado
  // ANTES de arrancar la descarga+evaluación del grafo de la app.
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      void import('./bootstrap').then((m) => m.mount(container, true))
    })
  })
} else {
  void import('./bootstrap').then((m) => m.mount(container, false))
}
